/* eslint-disable no-restricted-imports */
import type { Logger } from '@feature-hub/core';
import { getConsumptionsAndEmissionsWithIdentifiers } from '@oneaudi/feature-app-utils';
import type { VueFormatterFeatureServiceInterfaceV1 } from '@oneaudi/vue-formatter-service/lib/cjs/v1/vue-formatter-feature-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { EditorJsonContent } from './contexts/ContentInterfaces';

type WltpEntry = { formattedConsumption?: unknown; formattedEmission?: unknown } | undefined;
type WltpLogCollector = (message: string) => void;

type CaeMissingEvent = {
  event: 'CAE_MISSING';
  fa: typeof __FEATURE_APP_NAME__;
  carlineId: string;
  market: string;
  page: string;
  timestamp: string;
};

const hasEmptyStringKey = (wltpKey: unknown): boolean => {
  if (Array.isArray(wltpKey)) {
    return wltpKey.some((value) => value === '');
  }
  return wltpKey === '';
};

const hasRequiredWltpValues = (entry: WltpEntry) => {
  const { formattedConsumption, formattedEmission } = entry || {};
  return Boolean(formattedConsumption && formattedEmission);
};

const normalizeWltpKeys = (keys: unknown): string[] => {
  if (Array.isArray(keys)) {
    return keys
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  if (typeof keys === 'string') {
    const trimmed = keys.trim();
    return trimmed ? [trimmed] : [];
  }

  return [];
};

const getPagePath = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

const buildCaeMissingMessage = (carlineId: string, localeService: LocaleServiceV1): string => {
  const payload: CaeMissingEvent = {
    event: 'CAE_MISSING',
    fa: __FEATURE_APP_NAME__,
    carlineId,
    market: localeService.market || localeService.countryCode || '',
    page: getPagePath(),
    timestamp: new Date().toISOString(),
  };
  return JSON.stringify(payload);
};

const logCaeMissing = (
  carlineId: string,
  localeService: LocaleServiceV1,
  logger?: Pick<Logger, 'warn' | 'error'>,
  onCaeMissingLog?: WltpLogCollector,
): void => {
  const errorMsg = buildCaeMissingMessage(carlineId, localeService);
  if (onCaeMissingLog) onCaeMissingLog(errorMsg);
  if (logger) logger.error(errorMsg);
};

export const collectWltpKeys = (gallery: EditorJsonContent['gallery'] | undefined): string[] => {
  if (!gallery) return [];
  const allKeys = gallery.flatMap((item) => normalizeWltpKeys(item.legalText.wltpKey));
  return [...new Set(allKeys)];
};

export const getPerSlideWltpCompliance = async (
  gallery: EditorJsonContent['gallery'] | undefined,
  vueFormatterService: VueFormatterFeatureServiceInterfaceV1,
  localeService: LocaleServiceV1,
  logger?: Pick<Logger, 'warn' | 'error'>,
  onCaeMissingLog?: WltpLogCollector,
): Promise<boolean[]> => {
  if (!gallery) return [];

  const perSlideCompliance = new Array<boolean>(gallery.length).fill(true);
  const slideKeysByIndex: string[][] = Array.from({ length: gallery.length }, () => []);
  const validKeySlideIndices: number[] = [];
  const allKeysSet = new Set<string>();

  gallery.forEach((slide, index) => {
    const rawKeys = slide.legalText.wltpKey;
    if (hasEmptyStringKey(rawKeys)) {
      logCaeMissing('', localeService, logger, onCaeMissingLog);
      perSlideCompliance[index] = false;
      return;
    }

    const slideKeys = normalizeWltpKeys(rawKeys);
    slideKeysByIndex[index] = slideKeys;
    if (slideKeys.length > 0) {
      validKeySlideIndices.push(index);
      slideKeys.forEach((key) => allKeysSet.add(key));
    }
  });

  if (validKeySlideIndices.length === 0) {
    return perSlideCompliance;
  }

  try {
    const allKeys = [...allKeysSet];
    const result = await getConsumptionsAndEmissionsWithIdentifiers(
      allKeys,
      vueFormatterService,
      localeService,
    );
    validKeySlideIndices.forEach((slideIndex) => {
      const slideKeys = slideKeysByIndex[slideIndex];
      let slideCompliance = true;

      slideKeys.forEach((key) => {
        if (!hasRequiredWltpValues(result[key])) {
          slideCompliance = false;
          logCaeMissing(key, localeService, logger, onCaeMissingLog);
        }
      });

      perSlideCompliance[slideIndex] = slideCompliance;
    });
    if (typeof window !== 'undefined') {
      console.log('📊 WLTP Compliance:', {
        allKeys,
        result,
        perSlideCompliance,
      });
    }

    return perSlideCompliance;
  } catch (error) {
    if (typeof window !== 'undefined') {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn('⚠️ WLTP Compliance check failed:', errorMessage);
    }
    validKeySlideIndices.forEach((slideIndex) => {
      slideKeysByIndex[slideIndex].forEach((key) => {
        logCaeMissing(key, localeService, logger, onCaeMissingLog);
      });
      perSlideCompliance[slideIndex] = false;
    });

    return perSlideCompliance;
  }
};
